import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useOnboardingFormStore } from '@/lib/onboarding-form-store';
import { useAppForm } from '@/lib/use-app-form';
import { Card, CardContent, CardHeader, CardTitle } from '@worldcoo/ui/components/card';
import { useMutation, useQuery } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { generateCaptchaTokenResult } from '@/lib/captcha';
import { uploadOnboardingFiles } from '@/lib/upload-to-s3';
import { toast } from '@/lib/toast';
import { step5Schema } from '@/lib/onboarding-schemas';

export const Route = createFileRoute('/ngos/onboarding/terms')({
	component: RouteComponent,
});

function RouteComponent() {
	const { t, i18n } = useLingui();
	const navigate = useNavigate();
	const { formData, setFormData, setCurrentStep, setSubmitted } = useOnboardingFormStore();

	const termsLocale = (['en', 'es', 'ca', 'it'] as const).includes(i18n.locale as 'en' | 'es' | 'ca' | 'it')
		? (i18n.locale as 'en' | 'es' | 'ca' | 'it')
		: 'es';
	const { data: termsContent } = useQuery(trpc.onboarding.getTerms.queryOptions(termsLocale));
	const { mutateAsync: createDraft, isPending: isDraftPending } = useMutation(
		trpc.onboarding.createDraft.mutationOptions(),
	);
	const { mutateAsync: requestLogin } = useMutation(trpc.auth.requestLogin.mutationOptions());

	const form = useAppForm({
		defaultValues: {
			acceptConditions: formData.acceptConditions,
		},
		validators: {
			onSubmit: step5Schema,
		},
		onSubmit: async ({ value }) => {
			setFormData(value);

			try {
				const id = crypto.randomUUID();

				// Each upload mints its own captcha token.
				const uploaded = await uploadOnboardingFiles(
					{ logo: formData.logo, bankAccountCertificate: formData.bankAccountCertificate },
					id,
				);

				const { data: captchaToken } = await generateCaptchaTokenResult();
				if (!captchaToken) throw new Error('Failed to generate captcha token');
				const result = await createDraft({
					data: {
						...formData,
						...value,
						id,
						logo: uploaded.logo ?? '',
						bankAccountCertificate: uploaded.bankAccountCertificate ?? '',
					},
					captchaToken,
				});

				if (result && typeof result === 'object' && 'error' in result) {
					const errorMessages: Record<string, string> = {
						NETWORK_ERROR: t`Network error. Please check your connection and try again.`,
						SAVE_FAILED: t`Failed to save registration. Please try again.`,
					};
					toast.error(errorMessages[result.error as string] ?? t`Failed to submit registration. Please try again.`);
					return;
				}

				setSubmitted(true);
				setCurrentStep(6);
				toast.success(t`Registration submitted successfully`);
				if (formData.contact1Email) {
					// Best-effort: a missing captcha must not mask the successful registration with an error.
					const { data: loginCaptchaToken } = await generateCaptchaTokenResult();
					if (loginCaptchaToken) {
						await requestLogin({ email: formData.contact1Email, captchaToken: loginCaptchaToken });
					}
				}
				navigate({ to: '/ngos/onboarding/success', search: (prev) => prev });
			} catch (error) {
				console.error('[onboarding submit]', error);
				toast.error(t`Failed to submit registration. Please try again.`);
			}
		},
	});

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const action = (e.nativeEvent as SubmitEvent).submitter?.getAttribute('value');

		if (action === 'back') {
			setFormData(form.state.values);
			setCurrentStep(4);
			navigate({ to: '/ngos/onboarding/privacy', search: (prev) => prev });
			return;
		}

		form.handleSubmit();
	};

	return (
		<form id="onboarding-step-form" onSubmit={handleSubmit} className="flex flex-col gap-6">
			<Card className="shadow-warm">
				<CardHeader>
					<CardTitle className="text-xl">{t`Terms & Conditions`}</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-6">
					{termsContent ? (
						<div
							className="prose prose-sm flex max-h-100 max-w-none flex-col gap-3 overflow-y-auto rounded-lg border p-4 [&_a]:text-inherit [&_a]:underline [&_a]:underline-offset-2"
							dangerouslySetInnerHTML={{ __html: termsContent }}
						/>
					) : (
						<div className="flex h-40 items-center justify-center">
							<p className="text-muted-foreground text-sm">{t`Loading terms...`}</p>
						</div>
					)}

					<form.AppField
						name="acceptConditions"
						children={(field) => <field.CheckboxField label={t`I have read and accept the terms and conditions`} />}
					/>
					{isDraftPending && <p className="text-muted-foreground mt-4 text-sm">{t`Submitting your registration...`}</p>}
				</CardContent>
			</Card>
		</form>
	);
}
